import CalendarIcon from "@/assets/images/icons/calendar-icon.svg";
import NavIconLiveSession from "@/assets/images/icons/nav-icon-live-session.svg";
import PlayFooterReplay from "@/assets/images/icons/play-footer-replay.svg";
import ExternalLink from "@/components/ExternalLink";
import { Popover } from "@/components/ui/Popover";
import { Sheet, SheetDragHandle, SheetScrollView } from "@/components/ui/Sheet";
import { useLiveSessions } from "@/hooks/useLiveSessions";
import { URLS } from "@/lib/constants";
import { getProxiedMediaUrl } from "@/lib/media-proxy";
import { useIsMobile } from "@/lib/useDeviceType";
import type {
  LiveSessionDisplayEvent,
  MembershipTier,
} from "@/types/live-session";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Animated,
  Image,
  Platform,
  ScrollView,
  View,
} from "react-native";
import { SizableText, useMedia, XStack, YStack } from "tamagui";
import { BodyText, CardTitle, EyebrowText } from "@/components/ui/Typography";

const LIVE_ORANGE = "rgba(242, 102, 58, 1)";
const SURFACE_ALT_LIVE = "rgba(253, 233, 219, 1)";
const CONTENT_DEFAULT = "rgba(13, 13, 13, 1)";
const CONTENT_DEFAULT_INVERSE = "rgba(255, 255, 255, 1)";
const DROPDOWN_BG = "rgba(13, 13, 13, 1)";
const DROPDOWN_PANEL_WIDTH = 364;
const DROPDOWN_PANEL_MAX_HEIGHT = 556;
const HEADER_SURFACE_ALT = "#33312E";
const LIVE_SESSION_ROW_BG = DROPDOWN_BG;
const LIVE_SESSION_ROW_HOVER_BG = HEADER_SURFACE_ALT;
const SESSION_TEACHER_COLOR = CONTENT_DEFAULT_INVERSE;
const LIVE_CARD_PAD_V = 16;
const LIVE_CARD_PAD_H = 20;
const LIVE_CARD_ROW_MIN_HEIGHT = 136;
const BORDER_HAIRLINE = "rgba(201, 197, 195, 0.6)";
const COLUMN_CHROME_FOOTER_H = 108;
const LIST_VIEWPORT_H = DROPDOWN_PANEL_MAX_HEIGHT - COLUMN_CHROME_FOOTER_H;
const FOOTER_LINK_COLOR = "#B69D85";
const SESSION_META_ROW_MIN_HEIGHT = 36;

function LiveBeaconPulse({ size = 10 }: { size?: number }) {
  const opacity = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.4,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ]),
      { iterations: -1 },
    );
    pulse.start();
    return () => pulse.stop();
  }, [opacity]);
  const half = size / 2;
  return (
    <Animated.View
      style={{
        width: size,
        height: size,
        borderRadius: half,
        backgroundColor: LIVE_ORANGE,
        opacity,
      }}
    />
  );
}

const TRIGGER_SLOT_MIN_HEIGHT = 24;

function LiveTriggerIconSlot({
  showBeacon,
  slotWidth,
  calendarIconSize,
  calendarColor = "white",
}: {
  showBeacon: boolean;
  slotWidth: number;
  calendarIconSize: number;
  calendarColor?: string;
}) {
  const slotHeight = Math.max(TRIGGER_SLOT_MIN_HEIGHT, calendarIconSize);
  return (
    <View
      style={{
        width: slotWidth,
        height: slotHeight,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showBeacon ? (
        <LiveBeaconPulse size={10} />
      ) : (
        <NavIconLiveSession
          width={calendarIconSize}
          height={calendarIconSize}
          color={calendarColor}
        />
      )}
    </View>
  );
}

type Props = {
  membershipTier?: MembershipTier;
  membershipLoading?: boolean;
  iconSize?: number;
  showLabel?: boolean;
  /** Controlled open state (used when triggered externally, e.g. bottom bar). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render only the sheet, no trigger (controlled from outside). */
  hideTrigger?: boolean;
};

function getEventTierLabel(event: { is_only_for_pro: boolean }): string {
  return event.is_only_for_pro ? "OS Pro" : "Standard";
}

function resolveLiveSessionAvatarUrl(
  raw: string | false | undefined,
): string | null {
  if (typeof raw !== "string" || !raw) return null;
  return getProxiedMediaUrl(raw);
}

function SessionRow({
  event,
  onClose,
}: {
  event: LiveSessionDisplayEvent;
  onClose: () => void;
}) {
  const rawAvatar =
    typeof event.teacher_event_avatar === "string"
      ? event.teacher_event_avatar
      : typeof event.teacher_avatar === "string"
        ? event.teacher_avatar
        : null;
  const avatarUrl = rawAvatar ? resolveLiveSessionAvatarUrl(rawAvatar) : null;

  return (
    <ExternalLink url={event.url} onPress={onClose}>
      <YStack
        backgroundColor={LIVE_SESSION_ROW_BG}
        hoverStyle={{ backgroundColor: LIVE_SESSION_ROW_HOVER_BG }}
        pressStyle={{ backgroundColor: "rgba(255,255,255,0.06)" }}
        paddingTop={LIVE_CARD_PAD_V}
        paddingBottom={LIVE_CARD_PAD_V}
        paddingLeft={LIVE_CARD_PAD_H}
        paddingRight={LIVE_CARD_PAD_H}
        gap={12}
        flexDirection="column"
        alignItems="stretch"
        borderRadius={0}
        cursor={Platform.OS === "web" ? "pointer" : undefined}
        width="100%"
        minHeight={LIVE_CARD_ROW_MIN_HEIGHT}
      >
        <XStack
          ai="center"
          gap={8}
          flexWrap="nowrap"
          minHeight={SESSION_META_ROW_MIN_HEIGHT}
        >
          <View
            style={{
              flexShrink: 0,
              backgroundColor: event.isLive
                ? SURFACE_ALT_LIVE
                : CONTENT_DEFAULT_INVERSE,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 6,
              minWidth: 52,
              alignItems: "center",
              justifyContent: "center",
              minHeight: SESSION_META_ROW_MIN_HEIGHT - 8,
            }}
          >
            {event.isLive ? (
              <XStack ai="center" gap={4}>
                <View
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: LIVE_ORANGE,
                  }}
                />
                <SizableText color={CONTENT_DEFAULT} size="$3" fontWeight="600">
                  Live
                </SizableText>
              </XStack>
            ) : event.isAboutToStart && event.minutesUntilStart != null ? (
              <SizableText color={CONTENT_DEFAULT} size="$3" fontWeight="600">
                in {event.minutesUntilStart} min
              </SizableText>
            ) : (
              <SizableText color={CONTENT_DEFAULT} size="$3" fontWeight="600">
                {event.displayDate}
              </SizableText>
            )}
          </View>
          <EyebrowText
            color={CONTENT_DEFAULT_INVERSE}
            flexShrink={1}
            minWidth={0}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {event.displayTime}
          </EyebrowText>
          {event.is_only_for_pro ? (
            <View
              style={{
                flexShrink: 0,
                backgroundColor: "rgba(255,255,255,0.12)",
                paddingHorizontal: 6,
                paddingVertical: 2,
                borderRadius: 4,
              }}
            >
              <SizableText color="#aaa" size="$1" fontWeight="500">
                {getEventTierLabel(event)}
              </SizableText>
            </View>
          ) : null}
        </XStack>
        <XStack ai="flex-end" gap={12} flex={1}>
          <YStack f={1} minWidth={0} gap={8}>
            <CardTitle
              color="white"
              fontWeight="600"
              numberOfLines={1}
            >
              {event.title}
            </CardTitle>
            <BodyText
              color={SESSION_TEACHER_COLOR}
              numberOfLines={1}
            >
              {event.teacher_name}
            </BodyText>
          </YStack>
          {avatarUrl ? (
            <View
              style={{
                width: 64,
                height: 64,
                borderRadius: 32,
                backgroundColor: "#333",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {Platform.OS === "web" ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={avatarUrl}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Image
                  source={{ uri: avatarUrl }}
                  style={{ width: 64, height: 64 }}
                  resizeMode="cover"
                />
              )}
            </View>
          ) : null}
        </XStack>
      </YStack>
    </ExternalLink>
  );
}

function upcomingRowDividerStyle(inSheet: boolean) {
  return {
    height: 1,
    backgroundColor: BORDER_HAIRLINE,
    alignSelf: "stretch" as const,
    marginHorizontal: inSheet ? -16 : 0,
  };
}

function FooterExternalLink({
  url,
  label,
  onClose,
  shrinkText = false,
  icon = "calendar",
}: {
  url: string;
  label: string;
  onClose: () => void;
  shrinkText?: boolean;
  icon?: "calendar" | "play";
}) {
  const [hover, setHover] = useState(false);
  const isWeb = Platform.OS === "web";
  const accent = isWeb && hover ? CONTENT_DEFAULT_INVERSE : FOOTER_LINK_COLOR;
  const slot = icon === "play" ? 18 : 20;
  return (
    <ExternalLink
      url={url}
      onPress={onClose}
      onMouseEnter={isWeb ? () => setHover(true) : undefined}
      onMouseLeave={isWeb ? () => setHover(false) : undefined}
    >
      <XStack
        ai="center"
        jc="flex-start"
        gap={6}
        flexWrap="nowrap"
        style={isWeb ? { cursor: "pointer" } : undefined}
      >
        <YStack w={slot} h={slot} jc="center" ai="center" flexShrink={0}>
          {icon === "play" ? (
            <PlayFooterReplay width={18} height={18} color={accent} />
          ) : (
            <CalendarIcon width={20} height={20} color={accent} />
          )}
        </YStack>
        <EyebrowText
          color={accent}
          letterSpacing={0}
          textDecorationLine="underline"
          flexShrink={shrinkText ? 1 : 0}
          minWidth={shrinkText ? 0 : undefined}
        >
          {label}
        </EyebrowText>
      </XStack>
    </ExternalLink>
  );
}

function LiveSessionsContent({
  events,
  isLoading,
  error,
  fullScheduleUrl,
  onClose,
  membershipTier,
  inSheet = false,
}: {
  events: LiveSessionDisplayEvent[];
  isLoading: boolean;
  error: string | null;
  fullScheduleUrl: string;
  onClose: () => void;
  membershipTier: MembershipTier;
  inSheet?: boolean;
}) {
  const upcomingList = (
    <>
      {events.length > 0 ? (
        events.map((event, index) => (
          <YStack key={event.id}>
            <SessionRow event={event} onClose={onClose} />
            {index < events.length - 1 ? (
              <View style={upcomingRowDividerStyle(inSheet)} />
            ) : null}
          </YStack>
        ))
      ) : isLoading ? (
        <YStack padding={24} ai="center">
          <BodyText color="#999" textAlign="center">
            Loading sessions...
          </BodyText>
        </YStack>
      ) : error ? (
        <YStack padding={24} ai="center">
          <BodyText color="#999" textAlign="center">
            Could not load sessions
          </BodyText>
        </YStack>
      ) : (
        <YStack padding={24} ai="center">
          <BodyText color="#999" textAlign="center">
            No upcoming sessions for this week
          </BodyText>
        </YStack>
      )}
    </>
  );

  const footerLinks = (
    <YStack gap={10} ai="flex-start">
      <FooterExternalLink
        url={fullScheduleUrl}
        label="See Full Schedule"
        onClose={onClose}
        shrinkText
      />
      <FooterExternalLink
        url={URLS.WORKSHOP_REPLAYS_LIST}
        label="More Replays"
        onClose={onClose}
        icon="play"
      />
      {membershipTier === "pro" ? (
        <FooterExternalLink
          url={URLS.WORKSHOP_REPLAYS_CURRENT_SEASON}
          label="OS Pro GPS Replays"
          onClose={onClose}
          icon="play"
        />
      ) : null}
    </YStack>
  );

  const panelFooter = (
    <YStack
      minHeight={inSheet ? undefined : COLUMN_CHROME_FOOTER_H}
      paddingTop={16}
      paddingHorizontal={20}
      paddingBottom={20}
      borderTopWidth={1}
      borderTopColor={BORDER_HAIRLINE}
      ai="flex-start"
      jc="flex-start"
      backgroundColor={DROPDOWN_BG}
    >
      {footerLinks}
    </YStack>
  );

  if (inSheet) {
    return (
      <View
        style={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          backgroundColor: DROPDOWN_BG,
        }}
      >
        <SheetScrollView
          style={{ flex: 1, minHeight: 0, backgroundColor: DROPDOWN_BG }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
          bounces={false}
        >
          <YStack backgroundColor={DROPDOWN_BG}>{upcomingList}</YStack>
        </SheetScrollView>
        {panelFooter}
      </View>
    );
  }

  return (
    <YStack
      backgroundColor={DROPDOWN_BG}
      borderRadius={0}
      width={DROPDOWN_PANEL_WIDTH}
      maxWidth={DROPDOWN_PANEL_WIDTH}
      height={DROPDOWN_PANEL_MAX_HEIGHT}
      maxHeight={DROPDOWN_PANEL_MAX_HEIGHT}
      overflow="hidden"
    >
      <ScrollView
        style={{
          width: DROPDOWN_PANEL_WIDTH,
          height: LIST_VIEWPORT_H,
          backgroundColor: DROPDOWN_BG,
        }}
        contentContainerStyle={
          Platform.OS === "web" ? undefined : { flexGrow: 1 }
        }
        showsVerticalScrollIndicator
        bounces={false}
      >
        <YStack backgroundColor={DROPDOWN_BG}>{upcomingList}</YStack>
      </ScrollView>

      {panelFooter}
    </YStack>
  );
}

export function LiveSessionsDropdown({
  membershipTier = "regular",
  membershipLoading = false,
  iconSize: iconSizeProp,
  showLabel = true,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: Props) {
  const media = useMedia();
  const isMobile = useIsMobile();
  const iconSize = iconSizeProp ?? (media.gtMd ? 24 : 22);
  const [modalOpen, setModalOpen] = useState(false);
  const [sheetOpenState, setSheetOpenState] = useState(false);
  const [navHover, setNavHover] = useState(false);
  const isWeb = Platform.OS === "web";

  // When controlled (bottom bar), open state comes from props.
  const sheetOpen = controlledOpen ?? sheetOpenState;
  const setSheetOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) setSheetOpenState(open);
      onOpenChange?.(open);
    },
    [controlledOpen, onOpenChange],
  );

  const {
    events: liveEvents,
    isLoading,
    error,
    isLiveOrUpcoming,
    fullScheduleUrl,
  } = useLiveSessions({
    membershipTier,
    enabled: !membershipLoading,
  });

  const events = liveEvents;

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setSheetOpen(false);
  }, [setSheetOpen]);

  const handleSheetOpenChange = useCallback(
    (open: boolean) => {
      if (membershipLoading) return;
      if (open === sheetOpen) return;
      setSheetOpen(open);
      if (open) setModalOpen(false);
    },
    [membershipLoading, sheetOpen, setSheetOpen],
  );

  const calendarIconSize = iconSize;

  if (membershipTier === "non_member") {
    // Controlled/triggerless instances never render a trigger.
    if (hideTrigger) return null;
    const nonMemberAccent = isWeb && navHover ? LIVE_ORANGE : "white";
    return (
      <ExternalLink
        url={fullScheduleUrl}
        onMouseEnter={isWeb ? () => setNavHover(true) : undefined}
        onMouseLeave={isWeb ? () => setNavHover(false) : undefined}
      >
        <View
          style={{
            borderRadius: 6,
            paddingVertical: 2,
            paddingHorizontal: 2,
          }}
        >
          <XStack
            ai="center"
            gap={6}
            style={isWeb ? { color: nonMemberAccent } : undefined}
          >
            <LiveTriggerIconSlot
              showBeacon={false}
              slotWidth={iconSize}
              calendarIconSize={calendarIconSize}
              calendarColor={nonMemberAccent}
            />
            {showLabel && (
              <CardTitle
                display="none"
                $gtSm={{ display: "flex" }}
                color={nonMemberAccent}
                fontWeight="600"
                style={{ cursor: "pointer" }}
              >
                Live Sessions
              </CardTitle>
            )}
          </XStack>
        </View>
      </ExternalLink>
    );
  }

  const showBeacon = !membershipLoading && isLiveOrUpcoming;
  const liveLabelColor = showBeacon
    ? LIVE_ORANGE
    : isWeb && navHover
      ? LIVE_ORANGE
      : "white";
  const liveCalendarColor =
    !showBeacon && isWeb && navHover ? LIVE_ORANGE : "white";

  const triggerContent = (
    <XStack ai="center" gap={6} cursor="pointer">
      {showBeacon && isMobile ? (
        <View
          style={{
            backgroundColor: SURFACE_ALT_LIVE,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 6,
            minWidth: 52,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <XStack ai="center" gap={4}>
            <LiveBeaconPulse size={10} />
            <SizableText color={CONTENT_DEFAULT} size="$3" fontWeight="600">
              Live
            </SizableText>
          </XStack>
        </View>
      ) : (
        <LiveTriggerIconSlot
          showBeacon={showBeacon}
          slotWidth={iconSize}
          calendarIconSize={calendarIconSize}
          calendarColor={liveCalendarColor}
        />
      )}
      {showLabel && (
        <CardTitle
          display="none"
          $gtSm={{ display: "flex" }}
          color={liveLabelColor}
          fontWeight="600"
        >
          Live Sessions
        </CardTitle>
      )}
    </XStack>
  );

  const trigger = isWeb ? (
    <div
      style={{
        borderRadius: 6,
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 2,
        paddingRight: 2,
        pointerEvents: membershipLoading ? "none" : "auto",
        display: "flex",
        alignItems: "center",
      }}
      onMouseEnter={membershipLoading ? undefined : () => setNavHover(true)}
      onMouseLeave={membershipLoading ? undefined : () => setNavHover(false)}
    >
      {triggerContent}
    </div>
  ) : (
    <View
      style={{
        borderRadius: 6,
        paddingVertical: 2,
        paddingHorizontal: 2,
        pointerEvents: membershipLoading ? "none" : "auto",
      }}
    >
      {triggerContent}
    </View>
  );

  const content = (
    <LiveSessionsContent
      events={events}
      isLoading={isLoading}
      error={error}
      fullScheduleUrl={fullScheduleUrl}
      onClose={handleClose}
      membershipTier={membershipTier}
    />
  );

  const sheetNode = (
    <Sheet
      open={(hideTrigger || isMobile) && sheetOpen && !membershipLoading}
      onOpenChange={handleSheetOpenChange}
      snapPoints={[85, 50]}
      backgroundColor={DROPDOWN_BG}
    >
      <YStack f={1} backgroundColor={DROPDOWN_BG}>
        <SheetDragHandle onDarkBackground />
        <LiveSessionsContent
          events={events}
          isLoading={isLoading}
          error={error}
          fullScheduleUrl={fullScheduleUrl}
          onClose={handleClose}
          membershipTier={membershipTier}
          inSheet
        />
      </YStack>
    </Sheet>
  );

  if (hideTrigger) {
    return sheetNode;
  }

  return (
    <>
      <Popover
        open={!isMobile && modalOpen && !membershipLoading}
        onOpenChange={(open) => {
          if (membershipLoading) return;
          if (open === modalOpen) return;
          setModalOpen(open);
          if (open) setSheetOpen(false);
        }}
        onMobileOpen={() => setSheetOpen(true)}
        placement="bottom"
        offset={30}
        width={DROPDOWN_PANEL_WIDTH}
        maxWidth={DROPDOWN_PANEL_WIDTH}
        maxHeight={DROPDOWN_PANEL_MAX_HEIGHT + 24}
        contentBackgroundColor={DROPDOWN_BG}
        content={
          <YStack
            backgroundColor={DROPDOWN_BG}
            borderRadius="$4"
            overflow="hidden"
            shadowColor="#000"
            shadowOffset={{ width: 0, height: 4 }}
            shadowOpacity={0.25}
            shadowRadius={16}
          >
            {content}
          </YStack>
        }
      >
        {trigger}
      </Popover>

      {sheetNode}
    </>
  );
}
